// src/components/tools/ToolForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Code, Server, Trash2, Plus,
  Settings, Save, Layout
} from 'react-feather';
import ParameterEditor from './ParameterEditor';
import BuiltinToolPicker from './BuiltinToolPicker';
// Make sure to import the CSS file
import '../../styles/tools.css'; 

const ToolForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel, 
  successMessage = null,
  errorMessage = null,
  isAdmin = false,
  submitting = false,
  onBuiltinLinked = null
}) => {
  // --- State (Your original logic) ---
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parameters: '{"type":"object","properties":{},"required":[]}',
    is_custom: true,
    server_url: '',
    verb: 'GET',
    secret_token: '',
    headers: {}
  });

  const [editorMode, setEditorMode] = useState('visual');
  const [parsedParameters, setParsedParameters] = useState({
    type: 'object',
    properties: {},
    required: []
  });

  // --- Effects ---
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        parameters: JSON.stringify(initialData.parameters, null, 2),
        is_custom: initialData.is_custom || false,
        server_url: initialData.configuration?.url || '',
        verb: initialData.configuration?.verb || 'GET',
        secret_token: initialData.configuration?.secret_token || '',
        headers: initialData.configuration?.headers || {}
      });
      
      try {
        setParsedParameters(initialData.parameters);
      } catch (error) {
        console.error('Error parsing parameters:', error);
      }
    }
  }, [initialData]);

  // --- Handlers ---
  const handleParametersChange = useCallback((updatedParameters) => {
    setParsedParameters(updatedParameters);
    setFormData(prev => ({
      ...prev,
      parameters: JSON.stringify(updatedParameters, null, 2)
    }));
  }, []);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    // Your Auto-detect path parameters logic
    if (name === 'server_url') {
      const pathParamsMatches = value.match(/{([^{}]+)}/g) || [];
      const currentPathParams = pathParamsMatches.map(param => param.slice(1, -1));
      const currentParamNames = new Set(currentPathParams);
      
      if (currentPathParams.length > 0 || Object.keys(parsedParameters.properties).some(propName => {
        const prop = parsedParameters.properties[propName];
        return prop.description && prop.description.startsWith('Path parameter from URL');
      })) {
        let newProperties = { ...parsedParameters.properties };
        let newRequired = [...(parsedParameters.required || [])];
        let wasUpdated = false;

        Object.keys(newProperties).forEach(propName => {
          const prop = newProperties[propName];
          if (prop.description && prop.description.startsWith('Path parameter from URL') && !currentParamNames.has(propName)) {
            delete newProperties[propName];
            newRequired = newRequired.filter(r => r !== propName);
            wasUpdated = true;
          }
        });

        currentPathParams.forEach(paramName => {
          if (!newProperties[paramName]) {
            newProperties[paramName] = { type: 'string', description: 'Path parameter from URL' };
            if (!newRequired.includes(paramName)) {
              newRequired.push(paramName);
            }
            wasUpdated = true;
          }
        });

        if (wasUpdated) {
          handleParametersChange({
            type: 'object',
            properties: newProperties,
            required: newRequired
          });
        }
      }
    }
  };

  const handleJsonParametersChange = (e) => {
    const jsonValue = e.target.value;
    setFormData({ ...formData, parameters: jsonValue });
    try {
      const parsed = JSON.parse(jsonValue);
      setParsedParameters(parsed);
    } catch (error) {
      // Allow typing invalid JSON, catch error silently or show UI error
    }
  };

  const handleHeadersChange = (e, key, isKey = false) => {
    const value = e.target.value;
    const headers = { ...formData.headers };
    if (isKey) {
      const oldValue = headers[key];
      delete headers[key];
      if (value) { headers[value] = oldValue || ''; }
    } else {
      headers[key] = value;
    }
    setFormData({ ...formData, headers });
  };

  const addHeader = () => {
    const headers = { ...formData.headers };
    const newKey = `header_${Object.keys(headers).length + 1}`;
    headers[newKey] = '';
    setFormData({ ...formData, headers });
  };

  const removeHeader = (key) => {
    const headers = { ...formData.headers };
    delete headers[key];
    setFormData({ ...formData, headers });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      const parsedParams = JSON.parse(formData.parameters);
      const toolData = {
        name: formData.name,
        description: formData.description,
        is_custom: formData.is_custom,
        parameters: parsedParams,
        configuration: {
          url: formData.server_url,
          verb: formData.verb,
          secret_token: formData.secret_token,
          headers: formData.headers
        }
      };
      onSubmit(toolData);
    } catch (error) {
      alert('Invalid JSON in parameters field. Please check your syntax.');
    }
  };

  const canChangeType = !initialData;
  // New built-in: pick from the platform's tools instead of defining an API.
  const isBuiltinPicker = canChangeType && !formData.is_custom;

  // --- Render ---
  return (
    <div className="tool-form-container fade-in">
      
      {/* Page Header */}
      <div className="form-header-wrapper">
        <h2 className="form-title">
          {initialData ? 'Edit Configuration' : 'Create New Tool'}
        </h2>
        <p className="form-subtitle">Define configuration, authentication, and parameters for your agent tool.</p>
      </div>
      
      {successMessage && <div className="save-message success">{successMessage}</div>}
      {errorMessage && <div className="save-message error">{errorMessage}</div>}
      
      <form onSubmit={handleSubmit}>
        
        {/* CARD 1: GENERAL INFO */}
        <div className="form-section-card">
          <div className="card-title-row">
            <Settings size={20} />
            <h3>General Information</h3>
          </div>

          {canChangeType && (
            <div className="tool-type-selector">
              {/* Built-in Option */}
              <div 
                className={`tool-type-option ${!formData.is_custom ? 'selected' : ''}`}
                onClick={() => setFormData({...formData, is_custom: false})}
              >
                <div className="radio-circle"><div className="inner"></div></div>
                <div>
                  <span className="tool-type-option-title">Built-in Tool</span>
                  <span className="tool-type-option-description">Attach a platform tool, such as web search</span>
                </div>
              </div>

              {/* Custom Tool Option */}
              <div 
                className={`tool-type-option ${formData.is_custom ? 'selected' : ''}`}
                onClick={() => setFormData({...formData, is_custom: true})}
              >
                <div className="radio-circle"><div className="inner"></div></div>
                <div>
                  <span className="tool-type-option-title">Custom API Tool</span>
                  <span className="tool-type-option-description">Connect to your own API endpoint</span>
                </div>
              </div>
            </div>
          )}

          {!isBuiltinPicker && (
          <div className="grid-container">
            <div className="input-group">
              <label htmlFor="name">Tool Name</label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange} 
                placeholder="e.g. StockPriceChecker" 
                required 
              />
            </div>
            
            <div className="input-group full-width">
              <label htmlFor="description">Description</label>
              <textarea 
                id="description" 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange} 
                placeholder="Describe what this tool does so the agent knows when to use it..." 
                rows="2" 
              />
            </div>
          </div>
          )}
        </div>

        {isBuiltinPicker && (
          <BuiltinToolPicker onCancel={onCancel} onLinked={onBuiltinLinked} />
        )}

        {!isBuiltinPicker && (<>
        {/* CARD 2: SERVER SETTINGS */}
        <div className="form-section-card">
          <div className="card-title-row">
            <Server size={20} />
            <h3>Connection Settings</h3>
          </div>
          
          <div className="grid-container">
            <div className="input-group full-width">
              <label htmlFor="server_url">Endpoint URL</label>
              <input 
                type="url" 
                id="server_url" 
                name="server_url" 
                value={formData.server_url} 
                onChange={handleInputChange} 
                placeholder="https://api.example.com/users/{userId}" 
                required 
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="verb">HTTP Method</label>
              <select id="verb" name="verb" value={formData.verb} onChange={handleInputChange}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            
            <div className="input-group">
              <label htmlFor="secret_token">Secret Token (Optional)</label>
              <input 
                type="password" 
                id="secret_token" 
                name="secret_token" 
                value={formData.secret_token} 
                onChange={handleInputChange} 
                placeholder="Bearer token or API Key" 
              />
            </div>
            
            {/* Headers Section */}
            <div className="input-group full-width">
              <div className="flex-between mb-2">
                <label style={{marginBottom:0}}>Custom Headers</label>
                <button type="button" className="btn-text-action" onClick={addHeader}>
                  <Plus size={14} /> Add Header
                </button>
              </div>
              
              <div className="headers-list">
                {Object.keys(formData.headers).length === 0 ? (
                  <div className="empty-headers-box">No custom headers defined.</div>
                ) : (
                  Object.keys(formData.headers).map(key => (
                    <div key={key} className="header-row">
                      <input 
                        type="text" 
                        value={key} 
                        onChange={(e) => handleHeadersChange(e, key, true)} 
                        placeholder="Key" 
                      />
                      <input 
                        type="text" 
                        value={formData.headers[key]} 
                        onChange={(e) => handleHeadersChange(e, key, false)} 
                        placeholder="Value" 
                      />
                      <button type="button" className="icon-btn-danger" onClick={() => removeHeader(key)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* CARD 3: PARAMETERS */}
        <div className="form-section-card">
          <div className="card-title-row space-between">
            <div className="flex-center">
              <Code size={20} />
              <h3>Parameters</h3>
            </div>
            <div className="editor-toggle-pill">
              <button 
                type="button" 
                className={editorMode === 'visual' ? 'active' : ''} 
                onClick={() => setEditorMode('visual')}
              >
                <Layout size={14} /> Visual
              </button>
              <button 
                type="button" 
                className={editorMode === 'json' ? 'active' : ''} 
                onClick={() => setEditorMode('json')}
              >
                <Code size={14} /> JSON
              </button>
            </div>
          </div>

          <div className="parameter-editor-wrapper">
            {editorMode === 'visual' ? (
              // We wrap your existing ParameterEditor to ensure it sits nicely
              <div className="visual-editor-container">
                 <ParameterEditor parameters={parsedParameters} onChange={handleParametersChange} />
              </div>
            ) : (
              <div className="json-editor-container">
                <textarea 
                  id="parameters" 
                  name="parameters" 
                  value={formData.parameters} 
                  onChange={handleJsonParametersChange} 
                  className="code-textarea" 
                  required 
                  spellCheck="false"
                />
              </div>
            )}
          </div>
        </div>
        
        {/* FOOTER ACTIONS */}
        <div className="form-actions-footer">
          <button 
            type="button" 
            className="secondary-button" 
            onClick={onCancel} 
            disabled={submitting}
          >
            Cancel
          </button>
          
          <button 
            type="submit" 
            className={`primary-button ${submitting ? 'is-loading' : ''}`}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (initialData ? 'Update Tool' : 'Create Tool')}
            <Save size={18} style={{marginLeft: 8}}/>
          </button>
        </div>

        </>)}
      </form>
    </div>
  );
};

export default ToolForm;
